/**
 * Envío AP-4 · carga una imagen de forma que el canvas pueda leer sus píxeles.
 * Primero directo con CORS (R2 público); si el bucket no responde con CORS,
 * se baja por el backend (`/api/files/stream`, con la sesión) como blob.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { proxyUrlFor } from '../../services/templateService';

const loadImg = (src, cors) => new Promise((resolve, reject) => {
  const img = new Image();
  if (cors) img.crossOrigin = 'anonymous';
  img.decoding = 'async';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('load'));
  img.src = src;
});

const readable = (img) => {
  try {
    const c = document.createElement('canvas');
    c.width = 1; c.height = 1;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, 1, 1);
    ctx.getImageData(0, 0, 1, 1);
    return true;
  } catch (_e) { return false; }
};

export async function loadEditableImage(url) {
  if (!url) throw new Error('Sin imagen');
  if (/^(data|blob):/.test(url)) return { img: await loadImg(url, false), revoke: null };
  try {
    const sep = url.includes('?') ? '&' : '?';
    // El parámetro evita reusar una copia en caché pedida sin CORS (por el <img> del preview).
    const img = await loadImg(`${url}${sep}sml_edit=1`, true);
    if (readable(img)) return { img, revoke: null };
  } catch (_e) { /* se intenta por el backend */ }
  const res = await axios.get(proxyUrlFor(url), { responseType: 'blob', timeout: 60000 });
  const obj = URL.createObjectURL(res.data);
  const img = await loadImg(obj, false);
  return { img, revoke: () => URL.revokeObjectURL(obj) };
}

export function useEditableImage(url) {
  const [state, setState] = useState({ img: null, status: url ? 'loading' : 'idle', error: null });
  useEffect(() => {
    if (!url) { setState({ img: null, status: 'idle', error: null }); return undefined; }
    let alive = true;
    let revoke = null;
    setState({ img: null, status: 'loading', error: null });
    loadEditableImage(url)
      .then((r) => {
        revoke = r.revoke;
        if (alive) setState({ img: r.img, status: 'ready', error: null });
        else if (revoke) revoke();
      })
      .catch(() => {
        if (alive) setState({ img: null, status: 'error', error: 'No se pudo abrir la imagen para editarla.' });
      });
    return () => { alive = false; if (revoke) revoke(); };
  }, [url]);
  return state;
}
